import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Area, AreaChart
} from 'recharts';
import {
  Zap, Leaf, DollarSign, Wrench, AlertTriangle, Clock,
  FileText, Activity, ChevronRight
} from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import KPICard from '../../components/KPICard';
import OnboardingWizard from '../../components/OnboardingWizard';
import { formatDistanceToNow } from 'date-fns';

const COLORS = { solar: '#F59E0B', wind: '#1D6B4E', hydro: '#3B82F6', battery: '#8B5CF6', grid: '#6B7280' };

export default function DashboardPage() {
  const [kpis, setKpis]               = useState(null);
  const [trend, setTrend]              = useState([]);
  const [health, setHealth]            = useState([]);
  const [activity, setActivity]        = useState([]);
  const [revenue, setRevenue]          = useState([]);
  const [deadlines, setDeadlines]      = useState([]);
  const [loading, setLoading]          = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    Promise.all([
      dashboardAPI.kpis(),
      dashboardAPI.productionTrend(),
      dashboardAPI.assetHealth(),
      dashboardAPI.activity(),
      dashboardAPI.revenueVsTarget(),
      dashboardAPI.regulatoryCountdown(),
    ]).then(([k, t, h, a, r, d]) => {
      setKpis(k.data.data);
      setTrend(t.data.data);
      setHealth(h.data.data);
      setActivity(a.data.data);
      setRevenue(r.data.data);
      setDeadlines(d.data.data);
      if (k.data.data.assets.total === 0) setShowOnboarding(true);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Pivot production trend data: date -> {solar, wind, hydro...}
  const trendPivot = React.useMemo(() => {
    const map = {};
    trend.forEach((row) => {
      const date = row.log_date?.split('T')[0];
      if (!map[date]) map[date] = { date };
      map[date][row.asset_type] = parseFloat(row.production_mwh);
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [trend]);

  const revenuePivot = revenue.map((r) => ({
    label: `${r.period_year}-${String(r.period_month).padStart(2, '0')}`,
    target: parseFloat(r.target_revenue),
    actual: parseFloat(r.actual_revenue),
  }));

  const healthColor = (c) => ({ green: 'bg-accent', amber: 'bg-warn', red: 'bg-danger' }[c] || 'bg-gray-300');

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {showOnboarding && <OnboardingWizard onComplete={() => { setShowOnboarding(false); window.location.reload(); }}/>}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500">Portfolio overview · {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Monthly Production"
          value={kpis ? kpis.production.monthly_mwh.toLocaleString(undefined, { maximumFractionDigits: 0 }) : null}
          unit="MWh"
          icon={Zap}
          color="primary"
          loading={loading}
          subtitle={kpis ? `${kpis.production.total_capacity_mw.toLocaleString()} MW total capacity` : ''}
        />
        <KPICard
          title="Monthly Revenue"
          value={kpis ? `€${(kpis.financial.monthly_revenue / 1000).toFixed(0)}k` : null}
          icon={DollarSign}
          color="green"
          loading={loading}
          subtitle={kpis ? `Target: €${(kpis.financial.target_revenue / 1000).toFixed(0)}k` : ''}
        />
        <KPICard
          title="Carbon vs Target"
          value={kpis?.carbon.progress_pct !== null && kpis ? `${kpis.carbon.progress_pct}%` : null}
          icon={Leaf}
          color={kpis?.carbon.progress_pct > 90 ? 'red' : 'green'}
          loading={loading}
          subtitle={kpis ? `${kpis.carbon.annual_tco2e.toLocaleString()} tCO2e YTD` : ''}
        />
        <KPICard
          title="Open Work Orders"
          value={kpis ? kpis.operations.open_work_orders : null}
          icon={Wrench}
          color={kpis?.operations.critical_alerts > 0 ? 'amber' : 'primary'}
          loading={loading}
          subtitle={kpis ? `${kpis.operations.critical_alerts} critical alerts` : ''}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Production Trend (30 Days)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trendPivot}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" fontSize={11} tickFormatter={(d) => d?.slice(5)} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              {Object.keys(COLORS).map((type) => (
                <Area key={type} type="monotone" dataKey={type} stackId="1" stroke={COLORS[type]} fill={COLORS[type]} fillOpacity={0.5} name={type} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Asset Health</h3>
          <div className="space-y-2.5 max-h-[260px] overflow-y-auto">
            {health.slice(0, 8).map((a) => (
              <div key={a.id} className="flex items-center gap-2.5">
                <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${healthColor(a.health_color)}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">{a.name}</p>
                  <p className="text-xs text-gray-400 capitalize">{a.asset_type} · {a.capacity_mw} MW</p>
                </div>
                {a.critical_alerts > 0 && (
                  <span className="text-xs text-danger font-medium">{a.critical_alerts} alert{a.critical_alerts > 1 ? 's' : ''}</span>
                )}
              </div>
            ))}
            {health.length === 0 && !loading && <p className="text-sm text-gray-400 text-center py-8">No assets registered yet</p>}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Revenue vs Target</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenuePivot}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `€${Number(v).toLocaleString()}`} />
              <Legend />
              <Bar dataKey="target" fill="#D1D5DB" name="Target" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" fill="#1D6B4E" name="Actual" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Clock size={15} /> Regulatory Deadlines
          </h3>
          <div className="space-y-2.5 max-h-[220px] overflow-y-auto">
            {deadlines.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <p className="text-gray-700 truncate">{d.title}</p>
                  <p className="text-xs text-gray-400">{d.jurisdiction}</p>
                </div>
                <span className={`text-xs font-semibold shrink-0 ml-2 ${d.days_remaining <= 7 ? 'text-danger' : d.days_remaining <= 30 ? 'text-warn' : 'text-gray-400'}`}>
                  {d.days_remaining}d
                </span>
              </div>
            ))}
            {deadlines.length === 0 && !loading && <p className="text-sm text-gray-400 text-center py-8">No upcoming deadlines</p>}
          </div>
        </div>
      </div>

      {/* Activity feed */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Activity size={15} /> Recent Activity
        </h3>
        <div className="space-y-3">
          {activity.map((a) => (
            <div key={a.id} className="flex items-center gap-3 text-sm">
              <div className="h-7 w-7 rounded-full bg-primary-50 text-primary-500 flex items-center justify-center shrink-0">
                <ChevronRight size={13} />
              </div>
              <p className="text-gray-700 flex-1">{a.description}</p>
              <span className="text-xs text-gray-400 shrink-0">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</span>
            </div>
          ))}
          {activity.length === 0 && !loading && <p className="text-sm text-gray-400 text-center py-8">No recent activity</p>}
        </div>
      </div>
    </div>
  );
}
